import { useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import type { CustomUser } from 'api-client';
import { useAppContext } from '@/context';
import { ImpersonationPanel } from '@/support/ImpersonationPanel';
import { readCsrfToken, readImpersonation } from '@/support/session';

export default function UserImpersonation() {
  const { user, apiClients } = useAppContext();
  const [users, setUsers] = useState<CustomUser[]>([]);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<number>(0);
  const impersonating = readImpersonation() !== null;
  const authorized = Boolean(user?.isActive && user.isStaff && user.isSuperuser && !impersonating);

  useEffect(() => {
    if (!authorized) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClients.users.usersAdminUsersList({ search, page, ordering: 'email' }).then((result) => {
      if (cancelled) return;
      setUsers(result.results);
      setHasNext(Boolean(result.next));
    }).catch(() => {
      if (!cancelled) setError('Unable to load users. Retry to continue.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [apiClients.users, authorized, search, page, attempt]);

  if (!authorized) {
    return <Box>
      <Typography component="h1" variant="h4" gutterBottom>Support</Typography>
      <Alert severity="warning">{impersonating
        ? 'End the current impersonation before selecting another user.'
        : 'Support requires an active staff and superuser account.'}</Alert>
    </Box>;
  }

  return <ImpersonationPanel users={users} currentUserId={user!.id} csrfToken={readCsrfToken()}
    loading={loading} error={error} search={search} page={page} hasNext={hasNext}
    onSearch={(value) => { setSearch(value); setPage(1); }} onPage={setPage}
    onRetry={() => setAttempt((value) => value + 1)} />;
}
